import { ArrowLeft, Layers, AlertTriangle, FlaskConical, Archive, RefreshCw } from "lucide-react";
import type { BatchDetailResponse } from "../../gen/models";
import {
  useBatchCurationRun,
  BatchTelemetryRow,
} from "./BatchCurationRunInfo";

export function getBatchStatusBadgeClass(status?: string): string {
  switch (status) {
    case "collecting":
      return "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border-stone-200 dark:border-stone-700";
    case "processing":
      return "bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800";
    case "review_ready":
      return "bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800";
    case "completed":
      return "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800";
    case "archived":
      return "bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 border-stone-200 dark:border-stone-700";
    default:
      return "bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400";
  }
}

interface BatchDetailHeaderProps {
  batch: BatchDetailResponse | null;
  loading: boolean;
  onRefresh: () => void;
  onOpenSimulator?: () => void;
  onOpenArchiveModal?: () => void;
  onBack?: () => void;
}

export function BatchDetailHeader({
  batch,
  loading,
  onRefresh,
  onOpenSimulator,
  onOpenArchiveModal,
  onBack,
}: BatchDetailHeaderProps) {
  const curationTelemetry = useBatchCurationRun(batch);

  return (
    <div className="px-5 py-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between gap-3 shrink-0 bg-stone-50/50 dark:bg-stone-950/50">
      <div className="flex items-center gap-3 min-w-0">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="md:hidden p-1.5 rounded-lg border border-stone-200 dark:border-stone-800 text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 transition"
            title="Back to Batches"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        <div className="w-8 h-8 rounded-lg bg-stone-100 dark:bg-stone-800 text-stone-800 dark:text-stone-200 flex items-center justify-center shrink-0">
          <Layers className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-sm font-bold text-stone-900 dark:text-stone-100 truncate">
              {batch?.title || "Feedback Batch Review"}
            </h2>
            {batch && (
              batch.status === "review_ready" && batch.is_stale ? (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.2 rounded text-[10px] font-bold border uppercase tracking-wider shrink-0 bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800 ring-1 ring-rose-500/20"
                  title={batch.stale_reason || "Batch is outdated: one or more proposed changes has been altered by another version"}
                >
                  <AlertTriangle className="w-2.5 h-2.5 text-rose-500 shrink-0" />
                  <span>Outdated</span>
                </span>
              ) : (
                <span
                  className={`px-2 py-0.2 rounded text-[10px] font-semibold border uppercase tracking-wider shrink-0 ${getBatchStatusBadgeClass(
                    batch.status
                  )}`}
                >
                  {batch.status.replace("_", " ")}
                </span>
              )
            )}
          </div>

          <div className="flex items-center gap-2 text-[11px] text-stone-400 truncate mt-0.5 max-w-xl">
            {batch?.created_by_user_name && (
              <span>By <strong className="text-stone-700 dark:text-stone-300 font-medium">{batch.created_by_user_name}</strong></span>
            )}
            {batch?.created_at && (
              <span>• {new Date(batch.created_at).toLocaleDateString()}</span>
            )}
            {batch?.description && (
              <span>• {batch.description}</span>
            )}
          </div>

          {/* Processing Telemetry Row (Tokens, Cost, Model & Reviewer Run Trace) */}
          {(curationTelemetry.hasTelemetry || curationTelemetry.runId || curationTelemetry.model) && (
            <BatchTelemetryRow
              tokens={curationTelemetry.tokens}
              cost={curationTelemetry.cost}
              durationMs={curationTelemetry.durationMs}
              model={curationTelemetry.model}
              runId={curationTelemetry.runId}
              className="mt-1"
            />
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        {batch?.status === "review_ready" && onOpenSimulator && (
          <button
            type="button"
            onClick={onOpenSimulator}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 text-xs font-semibold hover:bg-purple-100 dark:hover:bg-purple-900 transition shadow-2xs cursor-pointer"
          >
            <FlaskConical className="w-3.5 h-3.5 text-purple-500" />
            <span>Simulate Batch</span>
          </button>
        )}

        {batch && batch.status !== "archived" && onOpenArchiveModal && (
          <button
            type="button"
            onClick={onOpenArchiveModal}
            title="Archive Batch"
            className="p-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 transition cursor-pointer"
          >
            <Archive className="w-3.5 h-3.5" />
          </button>
        )}

        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          title="Refresh Batch Details"
          className="p-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 transition disabled:opacity-50 cursor-pointer"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
    </div>
  );
}
